import { iconArrowRight } from "assets/icons";
import { Button } from "components/Button";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

export interface EventSession {
  sessionId: string;
  eventId: string;
  title: string;
  description: string;
  startTime: string;
  location: {
    locationId: string;
    name: string;
  };
  speakers: {
    registrationId: string;
    profile: {
      name: string;
    };
  }[];
  insertDate: string;
  updateDate: string;
}

interface SessionRowProps {
  title: string;
  description: string;
  startTime: string;
  locationName: string;
  speakers: any;
  actions: any;
}

interface Property {
  name: string;
  value: string;
}

export const useSessionsTable = (
  onEditSession?: (session: EventSession) => void
) => {
  const { id: eventId } = useParams<{ id: string }>();
  const { connectGraphQLApi, cancelRequest } = useGraphQLAuth();
  const { setToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [sessionRows, setSessionRows] = useState<SessionRowProps[]>([]);
  const [sessions, setSessions] = useState<EventSession[]>([]);
  const [searchText, setSearchText] = useState("");
  const [sessionsCount] = useState<number>(0);

  const searchTextFormRef = useRef<any>(null);

  /* HOOKS */
  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, []);

  useEffect(() => {
    getSessions();
  }, [searchText]);

  /* EVENT HANDLERS */
  const handleSearchTextFormSubmit = (event: any) => {
    event.preventDefault();
    const text = event.target.searchText.value;
    setSearchText(text);
  };

  const handleSearchTextRemove = (): void => {
    searchTextFormRef.current.reset();
    setSearchText("");
  };

  const getActionButtons = (session: EventSession) => {
    return [
      <Button
        key="edit"
        label=""
        size="small"
        icon={iconArrowRight}
        onlyIcon={true}
        onClick={() => onEditSession && onEditSession(session)}
        title="Editar Sessão"
      />,
    ];
  };

  /* API CALLS */
  const getSessions = async () => {
    setLoading(true);
    cancelRequest();

    const query = `
      query BackofficeSessions($filter: SessionFilterInput, $pagination: PaginationInput) {
        backofficeSessions(filter: $filter, pagination: $pagination) {
          sessionId
          eventId
          title
          description
          startTime
          durationMinutes
          location {
            locationId
            name
          }
          speakers {
            registrationId
            profile {
              name
            }
          }
          insertDate
          updateDate
        }
      }
    `;

    const filter = {
      eventId,
      searchText: searchText || undefined,
    };

    const variables = {
      filter,
      pagination: {
        offset: 0,
        limit: 1000, // Large limit to get all sessions for now
        sortBy: "startTime",
      },
    };

    try {
      const response = await connectGraphQLApi({
        query,
        variables,
        errorMessage: "Falha ao listar sessões",
        useNewApi: true,
      });

      setError(false);
      const responseData = (response as any).data;
      const eventSessions: EventSession[] =
        responseData.backofficeSessions || [];
      // Sort sessions by startTime
      const sortedSessions = eventSessions;
      console.log(sortedSessions);
      // Get count and calculate properties
      await getSessionsCount(sortedSessions);

      setSessions(sortedSessions);

      const sessionRows = sortedSessions.map((session: EventSession) => {
        const actions = getActionButtons(session);

        // Format startTime for display
        const startTimeFormatted = new Date(session.startTime).toLocaleString(
          "pt-BR",
          {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }
        );

        // Format speakers with links
        const speakersElement =
          session.speakers && session.speakers.length > 0 ? (
            session.speakers.map((speaker, index) => (
              <span
                key={speaker.registrationId}
                onClick={(e) => e.stopPropagation()}
              >
                <a
                  onClick={(e) => e.stopPropagation()}
                  href={`/events/${eventId}/registration/${speaker.registrationId}/edit`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#5056ff",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                >
                  {speaker?.profile?.name}
                </a>
                {index < session.speakers.length - 1 && ", "}
              </span>
            ))
          ) : (
            <span style={{ color: "#6c757d", fontStyle: "italic" }}>
              Sem palestrante
            </span>
          );

        return {
          title: session.title,
          description:
            session.description && session.description.length > 80
              ? `${session.description.substring(0, 80)}...`
              : session.description || "",
          startTime: startTimeFormatted,
          locationName: session.location?.name || "",
          speakers: speakersElement,
          actions,
        };
      });

      setSessionRows(sessionRows);
      setLoading(false);
    } catch (error: any) {
      console.error("Error details:", error);
      setError(true);
      setLoading(false);
      setToast({
        type: "error",
        message: "Falha ao listar sessões",
        isActive: true,
        timeToRemoveToast: 3000,
      });
    }
  };

  const getSessionsCount = async (sessions?: EventSession[]) => {
    try {
      const totalCount = sessions ? sessions.length : 0;

      // Create properties based on the sessions count
      const properties: Property[] = [
        { name: "Total de Sessões", value: totalCount.toString() },
      ];

      setProperties(properties);
    } catch (error: any) {
      console.error("Error getting sessions count:", error);
      // Don't show error toast for count, just log it
    }
  };

  /* EXPORT HANDLER */
  const handleExportCsv = async () => {
    try {
      const exportQuery = `
        query BackofficeSessionsExport($eventId: ID!) {
          backofficeSessionsExport(eventId: $eventId)
        }
      `;
      setToast({
        type: "warning",
        message:
          "Exportando dados... O arquivo será enviado para o seu e-mail!",
        isActive: true,
        timeToRemoveToast: 4000,
      });
      await connectGraphQLApi({
        query: exportQuery,
        variables: { eventId },
        errorMessage: "Erro ao exportar sessões",
        useNewApi: true,
      });
      setToast({
        type: "success",
        message: "A planilha será enviada para o seu e-mail em breve!",
        isActive: true,
        timeToRemoveToast: 4000,
      });
    } catch (error) {
      setToast({
        type: "error",
        message: "Erro ao exportar sessões.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
      console.error(error);
    }
  };

  return {
    loading,
    error,
    properties,
    sessionRows,
    sessions,
    eventId,
    getSessions,
    searchText,
    setSearchText,
    sessionsCount,
    searchTextFormRef,
    handleSearchTextFormSubmit,
    handleSearchTextRemove,
    handleExportCsv,
  };
};
